import React, { useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { usePageContextState } from "../../../context/PageContext";
import useWindowSize from "../../../hooks/useWindowSize";
import analytics from "../../../utils/analytics";
import { GetPathName, getStaticImg } from "../../../utils/function";
import { Text } from "../../atoms/index";
import { useSwipeable } from "react-swipeable";

import { Hidden } from "react-grid-system";
import styled from "styled-components";

// Styled component for the merchant section container
const MerchantSection = styled.div`
  padding: 64px 0 0 0;
  border-radius: 40px 40px 0 0;
  //   margin-top: 32px;
  position: relative;
  overflow: hidden;
  border-color: inherit;
  @media only screen and (min-width: 769px) {
    padding: 24px 0;
  }
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${(props) => getStaticImg("/bg/merch-bg-green.jpg")});
    background-size: cover;
    background-position: left;
    z-index: 0;

    @media only screen and (min-width: 769px) {
      background-position: top;
    }
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(31, 45, 61, 0.8);
    z-index: -1;
  }

  /* Linear gradient overlay for mobile */
  @media (max-width: 768px) {
    .gradient-overlay {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      height: 100%;
      background: linear-gradient(
        180deg,
        rgba(31, 45, 61, 0) -29%,
        #1f2d3d 175%,
        rgba(31, 45, 61, 0) 11%
      );
      z-index: 0;
      pointer-events: none;
    }
  }
`;

// Styled component for horizontal scrollable product list
const ProductList = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 16px;
  padding: 16px;
  margin: 0px 0px 24px 0px;
  @media only screen and (min-width: 769px) {
    margin: 24px 0;
  }
  //   scrollbar-width: none; /* Firefox */
  //   -ms-overflow-style: none; /* IE and Edge */

  //   &::-webkit-scrollbar {
  //     display: none; /* Chrome, Safari and Opera */
  //   }
`;

const ProductCard = styled.div`
  min-width: 184px;
  max-width: 184px;
  height: 293px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #f0f2f9;
  box-shadow:
    0px 1px 8px 0px rgba(0, 0, 0, 0.05),
    0px 3px 4px 0px rgba(0, 0, 0, 0.05),
    0px 3px 3px 0px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 184px;
  background-image: ${(props) => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  border-radius: 8px 8px 0 0;
`;

const ProductDetails = styled.div`
  padding: 16px 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProductTitle = styled.div`
  font-family: Gilroy, sans-serif;
  font-size: 12px;
  line-height: 1.33em;
  font-weight: 400;
  color: #1f2d3d;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductValue = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
`;

const ProductQuota = styled.span`
  font-family: Gilroy, sans-serif;
  font-size: 14px;
  line-height: 1.36em;
  font-weight: 700;
  color: #00bcf1;
`;

const ProductDivider = styled.span`
  font-family: Gilroy, sans-serif;
  font-size: 12px;
  line-height: 1.33em;
  font-weight: 400;
  color: #00bcf1;
`;

const ProductValidity = styled.span`
  font-family: Gilroy, sans-serif;
  font-size: 11px;
  line-height: 1.18em;
  font-weight: 400;
  color: #00bcf1;
`;

const ProductPrice = styled.div`
  font-family: Gilroy, sans-serif;
  font-size: 16px;
  line-height: 1.5em;
  font-weight: 700;
  color: #1f2d3d;
  letter-spacing: 0.5%;
`;

const SectionTitle = styled.h2`
  font-weight: 700;
  font-size: ${(props) => (props.isMobileView ? "32px" : "48px")};
  line-height: 1.2em;
  color: white;

  text-shadow:
    0px 3px 5px rgba(0, 0, 0, 0.05),
    0px 1px 18px rgba(0, 0, 0, 0.05),
    0px 6px 10px rgba(0, 0, 0, 0.05);

  @media only screen and (min-width: 769px) {
    line-height: 1.5em;
  }
`;

const SectionSubtitle = styled.h3`
  font-weight: 700;
  font-size: ${(props) => (props.isMobileView ? "14px" : "28px")};
  color: white;
`;

// Container for section titles
const HeaderContainer = styled.div`
  padding: 0 16px;
  position: relative;
  justify-content: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
`;

const NewFooter = ({ className = "" }) => {
  const { city } = useParams();
  const productListRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const screen = useWindowSize().size;
  const isMobileView = screen?.width <= 768;
  const { pageMode } = usePageContextState();
  const location = useLocation();
  const path = location?.pathname?.split("/");

  // Function to handle swiping between product cards
  const handleSwipe = (direction) => {
    if (!productListRef.current) return;

    const cardWidth = 200; // Approximate width of a card + gap
    const container = productListRef.current;

    if (direction === "left" && currentIndex < merchItems.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      container.scrollTo({
        left: newIndex * cardWidth,
        behavior: "smooth",
      });
    } else if (direction === "right" && currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      container.scrollTo({
        left: newIndex * cardWidth,
        behavior: "smooth",
      });
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleSwipe("left"),
    onSwipedRight: () => handleSwipe("right"),
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
  });

  // Function to handle clicking on a product
  const onPressPackage = (e) => {
    const beliPaketUrl = e?.link;
    const { eventName } = GetPathName({
      numberPath: 2,
      path,
    });
    const payload = {
      event: "pialabyu_packageBuyButton",
      city: city ?? "",
      package_id: e?.planId,
      package_name: e.title,
    };
    analytics.sendEvent(payload);
    window.open(beliPaketUrl, "_blank");
  };

  // Merchandise product list items
  const merchItems = [
    {
      id: 1,
      image: getStaticImg("/illustration/merch-item-1.png"),
      title: "Jersey SMBD Flex maverick",
      price: "Rp 265.000",
      validity: "30 Hari",
      quota: "42 GB",
      link: "https://www.byu.id/v2/ustore/detail/smbd-for-u-maverick-jersey-x-kaget-42-gb",
      planId: "mbd-for-u-blissful-bijou-jersey-x-kaget-42-gb",
    },
    {
      id: 2,
      image: getStaticImg("/illustration/merch-item-3.png"),
      title: "Jersey SMBD Trailblaze",
      price: "Rp 265.000",
      validity: "30 Hari",
      quota: "42 GB",
      link: "https://www.byu.id/v2/ustore/detail/smbd-for-u-trailblaze-jersey-x-kaget-42-gb",
      planId: "smbd-for-u-trailblaze-jersey-x-kaget-42-gb",
    },
    {
      id: 3,
      image: getStaticImg("/illustration/merch-item-2.png"),
      title: "Jersey SMBD Blissful Bijou",
      price: "Rp 295.000",
      validity: "30 Hari",
      quota: "42 GB",
      link: "https://www.byu.id/v2/ustore/detail/smbd-for-u-blissful-bijou-jersey-x-kaget-42-gb",
      planId: "smbd-for-u-blissful-bijou-jersey-x-kaget-42-gb",
    },

    {
      id: 4,
      image: getStaticImg("/illustration/merch-item-4.png"),
      title: "Tote Bag Exclusive by.U",
      price: "Rp 110.000",
      validity: "30 Hari",
      quota: "1 GB",
      link: "https://www.byu.id/v2/ustore/detail/totebag-exclusive-by.u-x-paket-data-1-gb-30-hari",
      planId: "totebag-exclusive-by.u-x-paket-data-1-gb-30-hari",
    },
    {
      id: 5,
      image: getStaticImg("/illustration/merch-item-5.png"),
      title: "Humblezing Foxtrot backpack",
      price: "Rp 349.000",
      validity: "30 Hari",
      quota: "20 GB",
      link: "https://www.byu.id/v2/ustore/detail/foxtrot-bagpack-for-u-x-kaget-20-gb",
      planId: "foxtrot-bagpack-for-u-x-kaget-20-gb",
    },
  ];

  return (
    <>
      <div className="new-footer-socmed z-10">
        <div style={{ display: "inline-flex", alignItems: "center" }}>
          <Text color="white" className="new-footer-socmed__text">
            Ikuti kami
          </Text>
          <Hidden xs sm>
            <img
              className="cursor-pointer"
              style={{ marginLeft: 8 }}
              src={getStaticImg("illustration/instagram-negative.svg")}
              alt="instagram"
              onClick={() =>
                window.open("https://www.instagram.com/byu.id/", "_blank")
              }
            />
            <img
              className="cursor-pointer"
              style={{ marginLeft: 8 }}
              src={getStaticImg("illustration/twitter-negative.svg")}
              alt="twitter"
              onClick={() =>
                window.open("https://twitter.com/byu_id", "_blank")
              }
            />
          </Hidden>
        </div>

        <Hidden md lg xl xxl>
          <div style={{ gap: 8, display: "flex", marginTop: 16 }}>
            <img
              className="cursor-pointer"
              src={getStaticImg("illustration/instagram-negative.svg")}
              alt="instagram"
              onClick={() =>
                window.open("https://www.instagram.com/byu.id/", "_blank")
              }
            />
            <img
              className="cursor-pointer"
              src={getStaticImg("illustration/twitter-negative.svg")}
              alt="twitter"
              onClick={() =>
                window.open("https://twitter.com/byu_id", "_blank")
              }
            />
          </div>
        </Hidden>
      </div>
      <div
        className="new-footer-copyright z-10"
        style={
          isMobileView && pageMode === "slick-scroll" && city !== "soc"
            ? { height: "20vh" }
            : !isMobileView
              ? { height: "10vh" }
              : { height: "auto" }
        }
      >
        <Text color="white" className="new-footer-copyright__text">
          Copyright © 2026 - by.U
        </Text>

        <Text color="white" className="new-footer-copyright__text">
          PT Telekomunikasi Selular
        </Text>
      </div>
    </>
  );
};

export default NewFooter;
